import React, { useEffect, useState } from 'react';
import { CURRENCY } from '../config';
import { getCachedSchoolName } from '../utils/branding';
import api from '../api/axios';

export default function BulkFeeChallan({ items, onClose }) {
  const [schoolName, setSchoolName] = useState(getCachedSchoolName());

  useEffect(() => {
    api.get('/public/branding')
      .then(r => {
        if (r.data?.school_name) setSchoolName(r.data.school_name);
      })
      .catch(() => {});
  }, []);

  if (!items || items.length === 0) return null;

  const handlePrint = () => window.print();

  const monthName = (d) =>
    d ? new Date(d).toLocaleDateString('en', { month: 'long', year: 'numeric' }) : '—';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-2 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white rounded shadow-xl w-full max-w-4xl my-4"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-3 border-b bg-gray-50 no-print sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <i className="fas fa-file-invoice-dollar text-blue-600"></i>
            <h2 className="font-semibold text-gray-800 text-sm">
              Bulk Challans <span className="text-gray-500">({items.length})</span>
            </h2>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handlePrint}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
            >
              <i className="fas fa-print mr-1"></i> Print / Save PDF
            </button>
            <button
              onClick={onClose}
              className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded text-sm hover:bg-gray-300"
            >
              Close
            </button>
          </div>
        </div>

        <div className="print-area p-4 sm:p-6 bg-white text-gray-900">
          {items.map(({ fee, student, previousDues = [] }, idx) => {
            const totalAmount = fee.amount || 0;
            const paidAmount = fee.paid_amount || 0;
            const balance = totalAmount - paidAmount;
            const isPaid = fee.status === 'Paid' || balance <= 0;

            const dues = isPaid ? [] : (Array.isArray(previousDues) ? previousDues : []);
            const totalDues = dues.reduce(
              (s, d) => s + ((d.amount || 0) - (d.paid_amount || 0)),
              0
            );
            const grandTotal = balance + totalDues;

            const challanNo = `CH-${String(fee.id).padStart(6, '0')}`;
            const issueDate = new Date().toISOString().split('T')[0];
            const monthLabel = monthName(fee.due_date);
            const docTitle = isPaid ? 'Payment Receipt' : 'Fee Challan';

            return (
              <div
                key={fee.id}
                className={idx < items.length - 1 ? 'challan-page mb-8' : 'challan-page'}
              >
                {/* HEADER */}
                <div className="flex items-start justify-between border-b-2 border-gray-800 pb-3 mb-4">
                  <div>
                    <h1 className="text-xl font-bold tracking-tight">{schoolName}</h1>
                    <p className="text-xs text-gray-600 mt-0.5">{student.campus_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">
                      {docTitle}
                    </p>
                    <p className="text-sm font-bold mt-0.5">{monthLabel}</p>
                    <p className="text-[11px] font-mono text-gray-600 mt-0.5">{challanNo}</p>
                  </div>
                </div>

                {/* META */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-[11px] mb-4">
                  <div>
                    <p className="text-gray-500 uppercase tracking-wide text-[9px] font-semibold">Student</p>
                    <p className="font-semibold text-sm mt-0.5">{student.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 uppercase tracking-wide text-[9px] font-semibold">Reg #</p>
                    <p className="font-mono mt-0.5">{student.registration_number || '—'}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 uppercase tracking-wide text-[9px] font-semibold">Class</p>
                    <p className="font-semibold mt-0.5">{student.class_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 uppercase tracking-wide text-[9px] font-semibold">Due Date</p>
                    <p className={`font-semibold mt-0.5 ${isPaid ? '' : 'text-red-700'}`}>
                      {fee.due_date || '—'}
                    </p>
                  </div>
                </div>

                {/* PAID strip */}
                {isPaid && (
                  <div className="mb-4 flex items-center justify-between border border-green-600 bg-green-50 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <i className="fas fa-check-circle text-green-700"></i>
                      <div className="text-xs">
                        <span className="font-bold text-green-800 uppercase tracking-wide">
                          Paid
                        </span>
                        <span className="text-green-700 ml-2">
                          {fee.paid_date || '—'}
                          {fee.payment_method && ` · ${fee.payment_method}`}
                        </span>
                      </div>
                    </div>
                    <p className="font-bold text-green-800 text-sm">
                      {CURRENCY}{paidAmount.toFixed(2)}
                    </p>
                  </div>
                )}

                {/* CURRENT FEE */}
                <table className="w-full border-collapse text-xs mb-3">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-300 px-2 py-1.5 text-left font-semibold">
                        Description
                      </th>
                      <th className="border border-gray-300 px-2 py-1.5 text-right font-semibold w-28">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-2 py-1.5">
                        {fee.fee_type} — {monthLabel}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-right">
                        {CURRENCY}{(fee.base_amount ?? fee.amount).toFixed(2)}
                      </td>
                    </tr>
                    {fee.discount_percentage > 0 && (
                      <tr>
                        <td className="border border-gray-300 px-2 py-1.5 text-gray-600">
                          Discount ({fee.discount_percentage}%)
                        </td>
                        <td className="border border-gray-300 px-2 py-1.5 text-right text-gray-600">
                          −{CURRENCY}
                          {(((fee.base_amount ?? fee.amount) * fee.discount_percentage) / 100).toFixed(2)}
                        </td>
                      </tr>
                    )}
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-2 py-1.5 font-semibold">
                        Current Month {isPaid ? 'Charge' : 'Payable'}
                      </td>
                      <td className="border border-gray-300 px-2 py-1.5 text-right font-semibold">
                        {CURRENCY}{totalAmount.toFixed(2)}
                      </td>
                    </tr>
                    {isPaid && (
                      <>
                        <tr>
                          <td className="border border-gray-300 px-2 py-1.5 text-green-700">
                            Amount Paid
                          </td>
                          <td className="border border-gray-300 px-2 py-1.5 text-right text-green-700">
                            {CURRENCY}{paidAmount.toFixed(2)}
                          </td>
                        </tr>
                        <tr className="bg-green-50">
                          <td className="border border-gray-300 px-2 py-1.5 font-bold text-green-800">
                            Balance
                          </td>
                          <td className="border border-gray-300 px-2 py-1.5 text-right font-bold text-green-800">
                            {CURRENCY}0.00
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>

                {/* DUES */}
                {!isPaid && dues.length > 0 && (
                  <>
                    <div className="mt-4 mb-1 flex items-center gap-2">
                      <i className="fas fa-exclamation-triangle text-amber-600 text-xs"></i>
                      <p className="text-xs font-bold text-amber-800 uppercase tracking-wide">
                        Previous Outstanding Dues
                      </p>
                    </div>
                    <table className="w-full border-collapse text-xs mb-3">
                      <thead>
                        <tr className="bg-amber-50">
                          <th className="border border-amber-300 px-2 py-1.5 text-left font-semibold">Month</th>
                          <th className="border border-amber-300 px-2 py-1.5 text-left font-semibold">Type</th>
                          <th className="border border-amber-300 px-2 py-1.5 text-right font-semibold">Total</th>
                          <th className="border border-amber-300 px-2 py-1.5 text-right font-semibold">Paid</th>
                          <th className="border border-amber-300 px-2 py-1.5 text-right font-semibold">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dues.map(d => {
                          const dBal = (d.amount || 0) - (d.paid_amount || 0);
                          return (
                            <tr key={d.id}>
                              <td className="border border-amber-200 px-2 py-1.5">
                                {monthName(d.due_date)}
                              </td>
                              <td className="border border-amber-200 px-2 py-1.5">{d.fee_type}</td>
                              <td className="border border-amber-200 px-2 py-1.5 text-right">
                                {CURRENCY}{(d.amount || 0).toFixed(2)}
                              </td>
                              <td className="border border-amber-200 px-2 py-1.5 text-right text-green-700">
                                {CURRENCY}{(d.paid_amount || 0).toFixed(2)}
                              </td>
                              <td className="border border-amber-200 px-2 py-1.5 text-right font-semibold text-red-700">
                                {CURRENCY}{dBal.toFixed(2)}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="bg-amber-50">
                          <td
                            colSpan="4"
                            className="border border-amber-300 px-2 py-1.5 font-semibold text-right"
                          >
                            Previous Dues Total
                          </td>
                          <td className="border border-amber-300 px-2 py-1.5 text-right font-bold text-red-700">
                            {CURRENCY}{totalDues.toFixed(2)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="mb-4 flex items-center justify-between border-2 border-red-600 bg-red-50 px-3 py-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">
                          Grand Total Payable
                        </p>
                        <p className="text-[11px] text-red-700">
                          Current {CURRENCY}{balance.toFixed(2)} + Dues {CURRENCY}{totalDues.toFixed(2)}
                        </p>
                      </div>
                      <p className="text-xl font-bold text-red-800">
                        {CURRENCY}{grandTotal.toFixed(2)}
                      </p>
                    </div>
                  </>
                )}

                {/* 3 COPIES */}
                <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-3 print:grid-cols-3 print:gap-2">
                  {['Bank Copy', 'School Copy', 'Student Copy'].map(copyLabel => (
                    <div
                      key={copyLabel}
                      className="relative overflow-hidden border border-gray-400 p-2 text-[10px] leading-tight break-inside-avoid"
                    >
                      {isPaid && (
                        <div className="paid-watermark pointer-events-none select-none">
                          <span>PAID</span>
                        </div>
                      )}

                      <div className="relative z-10">
                        <div className="flex justify-between items-center border-b border-gray-300 pb-1 mb-1.5">
                          <span className="font-bold text-[9px] uppercase tracking-widest">
                            {copyLabel}
                          </span>
                          <span className="font-mono text-[8px] text-gray-500">{challanNo}</span>
                        </div>

                        <p className="font-semibold text-[10px] truncate">{student.name}</p>
                        <p className="text-gray-600 text-[9px] truncate">
                          {student.class_name} · {student.registration_number || '—'}
                        </p>

                        <table className="w-full border-collapse text-[9px] my-1.5">
                          <tbody>
                            <tr>
                              <td className="border border-gray-300 px-1 py-0.5">
                                {fee.fee_type}
                              </td>
                              <td className="border border-gray-300 px-1 py-0.5 text-right">
                                {CURRENCY}{totalAmount.toFixed(2)}
                              </td>
                            </tr>
                            {dues.length > 0 && (
                              <tr>
                                <td className="border border-gray-300 px-1 py-0.5 text-amber-700">
                                  Prev. Dues ({dues.length})
                                </td>
                                <td className="border border-gray-300 px-1 py-0.5 text-right text-amber-700">
                                  {CURRENCY}{totalDues.toFixed(2)}
                                </td>
                              </tr>
                            )}
                            {isPaid ? (
                              <>
                                <tr>
                                  <td className="border border-gray-300 px-1 py-0.5 text-green-700">
                                    Paid
                                  </td>
                                  <td className="border border-gray-300 px-1 py-0.5 text-right text-green-700">
                                    {CURRENCY}{paidAmount.toFixed(2)}
                                  </td>
                                </tr>
                                <tr className="bg-green-50">
                                  <td className="border border-gray-300 px-1 py-0.5 font-bold">
                                    Balance
                                  </td>
                                  <td className="border border-gray-300 px-1 py-0.5 text-right font-bold">
                                    {CURRENCY}0.00
                                  </td>
                                </tr>
                              </>
                            ) : (
                              <tr className="bg-gray-100">
                                <td className="border border-gray-300 px-1 py-0.5 font-bold">
                                  {dues.length > 0 ? 'GRAND TOTAL' : 'TOTAL PAYABLE'}
                                </td>
                                <td className="border border-gray-300 px-1 py-0.5 text-right font-bold">
                                  {CURRENCY}{grandTotal.toFixed(2)}
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        <p className="text-[8px] text-gray-500">
                          Due: <strong>{fee.due_date}</strong>
                        </p>

                        <div className="pt-4 mt-1.5 border-t border-dashed border-gray-300">
                          <div className="border-t border-gray-400 w-16"></div>
                          <p className="text-[7px] text-gray-500 mt-0.5">
                            {isPaid ? 'Received By' : 'Bank / Cashier'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-4 pt-2 border-t border-gray-300 text-[9px] text-gray-600 space-y-0.5">
                  {isPaid ? (
                    <>
                      <p>
                        <strong>Note:</strong> Computer-generated payment receipt.
                        No signature required.
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>Note:</strong> Computer-generated challan. No signature
                        required for issuance.
                      </p>
                      <p>
                        Pay at the school office or at any designated bank branch.
                        {dues.length > 0 && (
                          <> Previous dues are included — pay the grand total to clear everything.</>
                        )}
                      </p>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}